import { Alert, Button, Stack } from "@mui/material";

import { useFollowPlayer } from "../hooks/useFollowPlayer";
import { GuardianAwareError } from "../guardian/GuardianAwareError";

export interface FollowPlayerButtonProps {
  playerId: string;
  playerName: string;
}

/** Directional follow action. Friendship remains a separate social workflow. */
export function FollowPlayerButton({
  playerId,
  playerName,
}: FollowPlayerButtonProps) {
  const { status, change } = useFollowPlayer(playerId);

  if (status.isLoading) {
    return (
      <Button
        aria-label={`Checking follow status for ${playerName}`}
        disabled
        size="small"
        variant="outlined"
      >
        Checking…
      </Button>
    );
  }

  if (status.isError || !status.data) {
    return (
      <Alert
        action={
          <Button color="inherit" onClick={() => status.refetch()} size="small">
            Try again
          </Button>
        }
        role="alert"
        severity="error"
      >
        Follow status is unavailable.
      </Alert>
    );
  }

  const isFollowing = status.data.is_following;
  const nextFollowing = !isFollowing;
  const actionLabel = `${isFollowing ? "Unfollow" : "Follow"} ${playerName}`;

  return (
    <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
      <Button
        aria-label={actionLabel}
        disabled={change.isPending}
        onClick={() => change.mutate(nextFollowing)}
        size="small"
        variant={isFollowing ? "outlined" : "contained"}
      >
        {change.isPending
          ? isFollowing
            ? "Unfollowing…"
            : "Following…"
          : isFollowing
            ? "Following"
            : "Follow"}
      </Button>
      {change.isError ? (
        <GuardianAwareError error={change.error}>
          <Alert role="alert" severity="error">
            Could not update this follow. Please try again.
          </Alert>
        </GuardianAwareError>
      ) : null}
    </Stack>
  );
}
